export interface FlattenedAnnotation {
  page?: number;
  fieldName?: string;
  fieldType: string | undefined;
  subtype?: string;
  buttonValue?: string;
  checkBox?: boolean;
  radioButton?: boolean;
  combo?: boolean;
  options?: { exportValue: string; displayValue: string }[];
  displayOrder: number | undefined | string;
  itemName: string | undefined;
  engineName: string | undefined;
  pdfName: string | undefined;
  contents?: string;
  action?: any;
}

export interface ValidationError {
  level?: 'error' | 'warning';
  message: string;
  page: number;
  annotation?: string;
}

export interface ValidationResult {
  errors: ValidationError[];
  totalAnnotations: number;
  totalErrors: number;
}
interface ExtractDisplayOrderAndItemName {
  displayOrder: number | undefined | string;
  itemName: string | undefined;
}

// ============ CONSTANTS ============
const VALIDATION_PATTERNS = {
  HALF_WIDTH_PERIOD: /\./,
  FULL_WIDTH_SPECIAL_CHARS: /[＝＠＃＊]/g,
  LINE_BREAK: /[\n\r\u2028\u2029]/,
  DIGITS_ONLY: /^\d+$/,
  AT_SYMBOL: /@/g,
  HASH_SYMBOL: /#/g,
  STAR_SYMBOL: /\*/g,
  EQUALS_SYMBOL: /=/g,
} as const;

const ERROR_MESSAGES = {
  HALF_WIDTH_PERIOD: `半角の"."が使用されています。`,
  FULL_WIDTH_SPECIAL_CHAR: (char: string) => `全角の"${char}"が使用されています。`,
  LINE_BREAK: `注釈に改行が含まれています。 .`,
  DUPLICATE_DISPLAY_ORDER: `表示順が重複しています。.`,
  DUPLICATE_ITEM_NAME: `項目名が重複しています。`,
  FIELD_NAME_NOT_FOUND_MESSAGE: 'フィールド名が存在しません',
  MULTIPLE_AT_SYMBOLS: `"@"記号が複数使用されています。1つだけ使用してください。`,
  MULTIPLE_HASH_SYMBOLS: `"#"記号が複数使用されています。1つだけ使用してください。`,
  MULTIPLE_STAR_SYMBOLS: `"*"記号が複数使用されています。1つだけ使用してください。`,
  MULTIPLE_EQUALS_SYMBOLS: `"="記号が複数使用されています。1つだけ使用してください。`,
  STAR_BEFORE_HASH: `"*"を"#"の前に置くことはできません。`,
} as const;

// ============ UTILITY FUNCTIONS ============
function removeSpaceAndNewlines(segment: string | undefined): string | undefined {
  if (!segment) return undefined;
  return segment.replace(/[ \r\n]/g, '');
}

function getAnnotationSegmentValue(text: string, delimiter: string): string | undefined {
  const lastDelimiterIndex = text.lastIndexOf(delimiter);
  return lastDelimiterIndex !== -1 ? removeSpaceAndNewlines(text.substring(lastDelimiterIndex + 1)) : undefined;
}

function removeAnnotationSegment(text: string, delimiter: string): string {
  const lastDelimiterIndex = text.lastIndexOf(delimiter);
  return lastDelimiterIndex !== -1 ? text.substring(0, lastDelimiterIndex) : text;
}

function extractDisplayOrderAndItemName(text: string): ExtractDisplayOrderAndItemName {
  const atIndex = text.indexOf('@');

  if (atIndex === -1) return { displayOrder: undefined, itemName: removeSpaceAndNewlines(text) };

  const orderString = removeSpaceAndNewlines(text.substring(0, atIndex));
  const itemName = removeSpaceAndNewlines(text.substring(atIndex + 1));

  if (!orderString) return { displayOrder: 0, itemName };

  if (VALIDATION_PATTERNS.DIGITS_ONLY.test(orderString)) return { displayOrder: Number(orderString), itemName };

  return { displayOrder: 'N/A', itemName };
}

// ============ ANNOTATION PARSING ============
export function parseAnnotationString(annotation: string): FlattenedAnnotation {
  const trimmedText = annotation?.trim();

  if (!trimmedText) {
    return {
      displayOrder: undefined,
      itemName: undefined,
      engineName: undefined,
      pdfName: undefined,
      fieldType: undefined,
    };
  }

  let remainingText = trimmedText;

  const fieldType = getAnnotationSegmentValue(remainingText, '=');
  remainingText = removeAnnotationSegment(remainingText, '=');

  const engineName = getAnnotationSegmentValue(remainingText, '*');
  remainingText = removeAnnotationSegment(remainingText, '*');

  const pdfName = getAnnotationSegmentValue(remainingText, '#');
  remainingText = removeAnnotationSegment(remainingText, '#');

  const { displayOrder, itemName } = extractDisplayOrderAndItemName(remainingText);

  return {
    displayOrder,
    itemName,
    engineName,
    pdfName,
    fieldType,
  };
}

function createValidationError(message: string, page: number, annotation: string, level: 'error' | 'warning' = 'error'): ValidationError {
  return {
    message,
    page,
    annotation,
    level,
  };
}

// ============ DUPLICATE VALIDATION ============
function findDuplicatesByDisplayOrder(annotations: FlattenedAnnotation[]): ValidationError[] {
  const errors: ValidationError[] = [];
  const groupsByDisplayOrder = new Map<number, FlattenedAnnotation[]>();

  annotations.forEach((annotation) => {
    const displayOrder = annotation.displayOrder;

    if (typeof displayOrder === 'number' && !isNaN(displayOrder) && displayOrder != 0) {
      if (!groupsByDisplayOrder.has(displayOrder)) {
        groupsByDisplayOrder.set(displayOrder, []);
      }
      groupsByDisplayOrder.get(displayOrder)!.push(annotation);
    }
  });

  groupsByDisplayOrder.forEach((annotationsWithSameOrder) => {
    if (annotationsWithSameOrder.length > 1) {
      annotationsWithSameOrder.forEach((annotation) => {
        errors.push(createValidationError(ERROR_MESSAGES.DUPLICATE_DISPLAY_ORDER, annotation.page!, annotation.contents!));
      });
    }
  });

  return errors;
}

function findDuplicatesByItemName(annotations: FlattenedAnnotation[]): ValidationError[] {
  const errors: ValidationError[] = [];
  const groupsByItemName = new Map<string, FlattenedAnnotation[]>();

  for (const annotation of annotations) {
    const itemName = annotation.itemName || '';

    if (itemName) {
      if (!groupsByItemName.has(itemName)) {
        groupsByItemName.set(itemName, []);
      }
      groupsByItemName.get(itemName)!.push(annotation);
    }
  }

  for (const annotationsWithSameName of groupsByItemName.values()) {
    if (annotationsWithSameName.length > 1) {
      for (const annotation of annotationsWithSameName) {
        errors.push(createValidationError(ERROR_MESSAGES.DUPLICATE_ITEM_NAME, annotation.page!, annotation.contents!));
      }
    }
  }

  return errors;
}

// ============ FORMAT VALIDATION ============
function safePushError(errors: ValidationError[], condition: boolean, message: string, page: number, annotation: string, level: 'error' | 'warning' = 'error') {
  if (condition) errors.push(createValidationError(message, page, annotation, level));
}

function validateAnnotationFormat(annotation: string, page: number, itemName: any): ValidationError[] {
  const errors: ValidationError[] = [];

  safePushError(errors, VALIDATION_PATTERNS.HALF_WIDTH_PERIOD.test(annotation), ERROR_MESSAGES.HALF_WIDTH_PERIOD, page, annotation);
  safePushError(errors, VALIDATION_PATTERNS.LINE_BREAK.test(annotation), ERROR_MESSAGES.LINE_BREAK, page, annotation);
  safePushError(errors, !itemName, ERROR_MESSAGES.FIELD_NAME_NOT_FOUND_MESSAGE, page, annotation);

  const fullWidthMatches = [...annotation.matchAll(VALIDATION_PATTERNS.FULL_WIDTH_SPECIAL_CHARS)];
  for (const match of fullWidthMatches) {
    errors.push(createValidationError(ERROR_MESSAGES.FULL_WIDTH_SPECIAL_CHAR(match[0]), page, annotation));
  }

  const specialCharErrors = validateSpecialCharacterCount(annotation, page);
  errors.push(...specialCharErrors);

  return errors;
}

function validateSpecialCharacterCount(annotation: string, page: number): ValidationError[] {
  const errors: ValidationError[] = [];

  const specialChars = [
    { pattern: VALIDATION_PATTERNS.AT_SYMBOL, message: ERROR_MESSAGES.MULTIPLE_AT_SYMBOLS },
    { pattern: VALIDATION_PATTERNS.HASH_SYMBOL, message: ERROR_MESSAGES.MULTIPLE_HASH_SYMBOLS },
    { pattern: VALIDATION_PATTERNS.STAR_SYMBOL, message: ERROR_MESSAGES.MULTIPLE_STAR_SYMBOLS },
    { pattern: VALIDATION_PATTERNS.EQUALS_SYMBOL, message: ERROR_MESSAGES.MULTIPLE_EQUALS_SYMBOLS },
  ];

  for (const { pattern, message } of specialChars) {
    const matches = annotation.match(pattern);
    if (matches && matches.length > 1) {
      errors.push(createValidationError(message, page, annotation));
    }
  }

  return errors;
}

export function groupAnnotationsByPage(annotations: FlattenedAnnotation[]): Record<number, FlattenedAnnotation[]> {
  return annotations.reduce((acc, annotation) => {
    if (typeof annotation.page !== 'number') return acc;
    if (!acc[annotation.page]) acc[annotation.page] = [];
    acc[annotation.page].push(annotation);
    return acc;
  }, {} as Record<number, FlattenedAnnotation[]>);
}

// ============ MAIN VALIDATION FUNCTION ============
function validateAnnotations(annotations: FlattenedAnnotation[]): ValidationError[] {
  const errors: ValidationError[] = [];

  for (const annotation of annotations) {
    if (typeof annotation.page !== 'number') continue;
    if (!annotation.contents) continue;
    const formatErrors = validateAnnotationFormat(annotation.contents, annotation.page, annotation.itemName);
    errors.push(...formatErrors);
  }

  const displayOrderErrors = findDuplicatesByDisplayOrder(annotations);
  const itemNameErrors = findDuplicatesByItemName(annotations);

  errors.push(...displayOrderErrors);
  errors.push(...itemNameErrors);

  return errors;
}

export function validateAnnotationsWithSummary(annotations: FlattenedAnnotation[] = []): ValidationResult {
  if (!annotations) {
    return {
      errors: [],
      totalAnnotations: 0,
      totalErrors: 0,
    };
  }

  const totalAnnotations = annotations.length;
  const errors = validateAnnotations(annotations);

  const totalErrors = errors.length;

  return {
    errors,
    totalAnnotations,
    totalErrors,
  };
}

export function checkFieldConflictsPerPage(annotations: FlattenedAnnotation[]): ValidationError[] {
  const errors: ValidationError[] = [];

  const annotationsByPage = groupAnnotationsByPage(annotations);

  for (const [pageStr, annotations] of Object.entries(annotationsByPage)) {
    const page = Number(pageStr);

    const textMap = new Map<string, FlattenedAnnotation[]>();
    const checkboxMap = new Map<string, FlattenedAnnotation[]>();
    const dropdownMap = new Map<string, FlattenedAnnotation[]>();
    const radioGroupMap = new Map<string, string[]>(); 


    for (const annotation of annotations) {
      const { fieldName, fieldType, subtype, checkBox, radioButton, combo, options } = annotation;
      if (!fieldName) continue;

      // Text
      if (fieldType === 'Tx') {
        pushToMapArray(textMap, fieldName, annotation);
      }

      // Checkbox
      if (fieldType === 'Btn' && subtype === 'Widget' && checkBox) {
        pushToMapArray(checkboxMap, fieldName, annotation);
      }

      if (fieldType === 'Ch' && combo) {
        pushToMapArray(dropdownMap, fieldName, annotation);

        if (Array.isArray(options)) {
          const countMap = new Map<string, number>();

          for (const option of options) {
            const val = option?.exportValue;
            if (!val) continue;

            countMap.set(val, (countMap.get(val) || 0) + 1);
          }

          for (const [val, count] of countMap.entries()) {
            if (count > 1) {
              errors.push({
                message: `ドロップダウン "${fieldName}" に重複するオプション "${val}" があります。`,
                page,
                annotation: fieldName,
                level: 'error',
              });
            }
          }
        }
      }

      // Radio
      if (fieldType === 'Btn' && subtype === 'Widget' && radioButton && annotation.buttonValue) {
        pushToMapArray(radioGroupMap, fieldName, annotation.buttonValue);
      }
    }

    collectDuplicateFieldNameErrors(textMap, 'テキストフィールド', errors, page);
    collectDuplicateFieldNameErrors(checkboxMap, 'チェックボックス', errors, page);
    collectDuplicateFieldNameErrors(dropdownMap, 'ドロップダウン', errors, page);


    collectGroupedStringValueDuplicates(
      radioGroupMap,
      (groupName, val) => `ラジオグループ "${groupName}" に重複する値 "${val}" があります。`,
      errors,
      page
    );
  }

  return errors;
}


function pushToMapArray<T>(map: Map<string, T[]>, key: string, value: T) {
  if (!map.has(key)) map.set(key, []);
  map.get(key)!.push(value);
}

function collectDuplicateFieldNameErrors(
  map: Map<string, FlattenedAnnotation[]>,
  fieldLabel: string,
  errors: ValidationError[],
  page: number
) {
  for (const [fieldName, annots] of map.entries()) {
    if (annots.length > 1) {
      errors.push({
        message: `${fieldLabel} "${fieldName}" が重複しています。`,
        page,
        annotation: fieldName,
        level: 'error',
      });
    }
  }
}


function collectGroupedStringValueDuplicates(
  map: Map<string, string[]>,
  messageBuilder: (groupName: string, value: string) => string,
  errors: ValidationError[],
  page: number
) {
  for (const [groupName, values] of map.entries()) {
    const countMap = new Map<string, number>();

    for (const value of values) {
      countMap.set(value, (countMap.get(value) || 0) + 1);
    }

    for (const [val, count] of countMap.entries()) {
      if (count > 1) {
        errors.push({
          message: messageBuilder(groupName, val),
          page,
          annotation: groupName,
          level: 'error',
        });
      }
    }
  }
}

